import { useDebounceFn } from '@vueuse/core'

import type { ComputedRef, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { useUsersStore } from './store'
import { useUsersApi } from '../api/list.hook'
import { toTeamOptions, useUserOptions } from './user-options'

const SEARCH_DEBOUNCE_MS = 400

interface FiltersUsersReturn {
  refSearch: WritableComputedRef<string>
  refRoles: WritableComputedRef<string[]>
  refTeams: WritableComputedRef<string[]>
  refStatus: WritableComputedRef<string | undefined>
  roleOptions: ComputedRef<AppSelectOption[]>
  statusOptions: ComputedRef<AppSelectOption[]>
  teamOptions: ComputedRef<AppSelectOption[]>
  isLoading: ComputedRef<boolean>
}

export const useFiltersUsers = (): FiltersUsersReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useUsersStore()
  const { roleOptions, statusOptions } = useUserOptions()

  const teamOptions = computed<AppSelectOption[]>(() => toTeamOptions(store.getTeams))

  /**
   * The one place a filter change becomes a request: write the store, push the query, refetch.
   * `page` is dropped so a new filter never lands on a page that no longer exists.
   */
  const syncQueryAndReload = async (): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        q: store.getFilterSearch || undefined,
        status: store.getFilterStatus || undefined,
        roles: store.getFilterRoles.length ? store.getFilterRoles : undefined,
        teams: store.getFilterTeams.length ? store.getFilterTeams : undefined,
        page: undefined,
      },
    })
    await useUsersApi()
  }

  const applySearch = useDebounceFn(() => syncQueryAndReload(), SEARCH_DEBOUNCE_MS)

  const refSearch = computed<string>({
    get: () => store.getFilterSearch ?? '',
    set: (value) => {
      store.setFilterSearch(value.trim() || null)
      void applySearch()
    },
  })

  const refRoles = computed<string[]>({
    get: () => store.getFilterRoles,
    set: (value) => {
      store.setFilterRoles(value)
      void syncQueryAndReload()
    },
  })

  const refTeams = computed<string[]>({
    get: () => store.getFilterTeams,
    set: (value) => {
      store.setFilterTeams(value)
      void syncQueryAndReload()
    },
  })

  const refStatus = computed<string | undefined>({
    get: () => store.getFilterStatus ?? undefined,
    set: (value) => {
      store.setFilterStatus(value ?? null)
      void syncQueryAndReload()
    },
  })

  const isLoading = computed<boolean>(() => store.isLoading)

  return {
    refSearch,
    refRoles,
    refTeams,
    refStatus,
    roleOptions,
    statusOptions,
    teamOptions,
    isLoading,
  }
}
